import { ChangeEvent, useCallback, useEffect } from 'react'
import { Radio } from './radio'
import { t } from '@lingui/core/macro'

export const ReviewType = {
  ALL: 'all',
  MATCH: 'match',
  NO_MATCH: 'no_match',
  REVIEWED: 'reviewed',
  ALL_EXCLUDE_CONFIDENCE: 'all_exclude_confidence',
  ALL_EXCLUDE_REVIEWED: 'all_exclude_reviewed',
} as const

export type ReviewType = (typeof ReviewType)[keyof typeof ReviewType]

interface PreReviewStepProps {
  isReviewed: boolean
  reviewType: ReviewType
  counts: Record<ReviewType, number>
  onReviewTypeChange: (value: ReviewType) => void
}

export const PreReviewStep = ({
  isReviewed,
  reviewType,
  counts,
  onReviewTypeChange,
}: PreReviewStepProps) => {
  const fullConfidenceMatchesCount = counts.all - counts.all_exclude_confidence

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onReviewTypeChange(event.target.value as ReviewType)
    },
    [onReviewTypeChange],
  )

  useEffect(() => {
    if (reviewType === ReviewType.ALL && isReviewed && counts.all_exclude_reviewed > 0) {
      onReviewTypeChange(ReviewType.ALL_EXCLUDE_REVIEWED)
    }
  }, [counts.all_exclude_reviewed]) // intentionally only this dependency

  return (
    <div className="pt-12">
      <h3 className="text-slate-800 font-bold text-[22px] mb-11 px-14">
        {t`Taxonomy translation review`}
      </h3>

      <div className="flex">
        <div className="basis-1/2 px-14">
          <p className="text-slate-800">
            {t`We’ll guide you through each line, allowing you to confirm, modify, and match fields. You can always update this later in the translation table. Claravine won’t alter your files; instead, a new translation file will be created.`}
          </p>
          <p className="text-slate-800 mt-6">
            {t`Completing the review process is essential to enhancing the quality of your future translations!`}
          </p>
        </div>
        <div className="basis-1/2 px-14 py-6 flex flex-col justify-center border-l-2 border-l-[#8052FF] bg-[#8052FF]/5">
          <p className="text-slate-800 font-medium mb-1">{t`What to review:`}</p>
          {isReviewed && counts.all_exclude_reviewed > 0 && (
            <label className="cursor-pointer flex items-center gap-2 mb-1">
              <Radio
                value={ReviewType.ALL_EXCLUDE_REVIEWED}
                checked={reviewType === ReviewType.ALL_EXCLUDE_REVIEWED}
                onChange={onChange}
              />
              <span className="text-slate-800 select-none">
                {`All items, minus ${counts.reviewed} already reviewed`}
              </span>
            </label>
          )}
          <label className="cursor-pointer flex items-center gap-2 mb-1">
            <Radio value="all" checked={reviewType === 'all'} onChange={onChange} />
            <span className="text-slate-800 select-none">{`All ${counts.all} items`}</span>
          </label>
          {counts.match > 0 && (
            <label className="cursor-pointer flex items-center gap-2 mb-1">
              <Radio value="match" checked={reviewType === 'match'} onChange={onChange} />
              <span className="text-slate-800 select-none">
                {`${counts.match} ${counts.match === 1 ? 'match' : 'matches'}`}
              </span>
            </label>
          )}
          {counts.no_match > 0 && (
            <label className="cursor-pointer flex items-center gap-2 mb-1">
              <Radio value="no_match" checked={reviewType === 'no_match'} onChange={onChange} />
              <span className="text-slate-800 select-none">{`${counts.no_match} without match`}</span>
            </label>
          )}
          {!isReviewed && counts.all_exclude_confidence > 0 && (
            <label className="cursor-pointer flex items-center gap-2 mb-1">
              <Radio
                value="all_exclude_confidence"
                checked={reviewType === 'all_exclude_confidence'}
                onChange={onChange}
              />
              <span className="text-slate-800 select-none">
                {`All items, minus ${fullConfidenceMatchesCount} full confidence ${fullConfidenceMatchesCount === 1 ? 'match' : 'matches'}`}
              </span>
            </label>
          )}
          {isReviewed && counts.reviewed > 0 && (
            <label className="cursor-pointer flex items-center gap-2 mb-1">
              <Radio
                value={ReviewType.REVIEWED}
                checked={reviewType === ReviewType.REVIEWED}
                onChange={onChange}
              />
              <span className="text-slate-800 select-none">{`${counts.reviewed} reviewed`}</span>
            </label>
          )}
        </div>
      </div>
    </div>
  )
}
